import { useEffect, useMemo, useState } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Stepper,
  Text,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconSearch, IconX } from '@tabler/icons-react'
import { formatPhone } from '@/utils'
import { useAutoPopulate } from './shared/useAutoPopulate'
import { useSignature } from './shared/useSignature'
import { useCanOrder } from './shared/useCanOrder'
import { useOrderSubmission } from './shared/useOrderSubmission'
import { useLastProgressNoteIndication } from './shared/useLastProgressNoteIndication'
import { useCptValueSet, type CptCode } from './shared/useCptValueSet'
import { NUCC_CLASSIFICATIONS } from './shared/nuccClassifications'
import { SUBMIT_PERMISSION_TOOLTIP, SIGNATURE_REQUIRED_TOOLTIP } from './shared/constants'
import { useSeedDiagnosisCodes } from './shared/useSeedDiagnosisCodes'
import { TooltipButton } from './shared/TooltipButton'
import { FormHeader } from './shared/FormHeader'
import { SignatureSection } from './shared/SignatureSection'
import { DiagnosisCodesSection } from './shared/DiagnosisCodesSection'
import { OrderFormLayout } from './shared/OrderFormLayout'
import { OrderCart } from './shared/OrderCart'
import type { CartItem } from './shared/OrderCart'
import { generateBeHumanOrderPdf } from './pdf/behumanPdfGenerator'
import { mapProcedureReferralToPdf } from './pdf/mapProcedureReferralToPdf'
import {
  PROCEDURE_REFERRAL_QUESTIONNAIRE_ID,
  GENERIC_REFERRAL_CPT,
  createInitialProcedureReferralFormState,
} from './procedureReferralFormData'
import type { ProcedureReferralFormState } from './procedureReferralFormData'
import type { AutoPopulateData } from './pdf/pdfTypes'
import { SpecialistPicker } from './referral/SpecialistPicker'
import { ReferralLetterFields } from './referral/ReferralLetterFields'
import { useReferralLetterState } from './referral/useReferralLetterState'
import { useWarnBeforeUnload } from '@/hooks/useWarnBeforeUnload'
import {
  distinctPracticeName,
  isValidFax,
  recipientFormData,
  recipientName,
  type ReferralSpecialtyCode,
} from './referral/referralTypes'

type Props = {
  patient: Patient
  onBack: () => void
}

type Step = 'specialist' | 'cptCodes' | 'details' | 'review'
const STEPS: Step[] = ['specialist', 'cptCodes', 'details', 'review']

const PROCEDURE_REQUIRED_TOOLTIP = 'Add a procedure on the Procedures step'
const DRAFTING_TOOLTIP = 'Wait for the AI draft to finish'

const specialtyOptions = NUCC_CLASSIFICATIONS.map((c) => ({ value: c.code, label: c.label }))

export const ProcedureReferralForm = ({ patient, onBack }: Props) => {
  const [step, setStep] = useState<Step>('specialist')
  const [cptSearch, setCptSearch] = useState('')

  const {
    patientDefaults,
    providerDefaults,
    insuranceDefaults,
    diagnosisDefaults,
    orgError,
    insuranceWarning,
  } = useAutoPopulate(patient)
  const signature = useSignature()
  const { canOrder: isPractitioner, loading: canOrderLoading } = useCanOrder()

  const autoPopulateData: AutoPopulateData = {
    patient: patientDefaults,
    provider: providerDefaults,
    insurance: insuranceDefaults,
  }

  const form = useForm<ProcedureReferralFormState>({
    initialValues: createInitialProcedureReferralFormState(),
    validateInputOnBlur: true,
  })

  const { suggestion: indicationSuggestion } = useLastProgressNoteIndication(patient.id)

  const { codes: cptCodesForSpecialty, loading: cptLoading } = useCptValueSet(
    form.values.specialtyCode || null,
  )

  const submission = useOrderSubmission({
    patientId: patient.id,
    questionnaireId: PROCEDURE_REFERRAL_QUESTIONNAIRE_ID,
    generatePdfBytes: () =>
      generateBeHumanOrderPdf(
        mapProcedureReferralToPdf(
          form.values,
          autoPopulateData,
          form.values.recipient ?? undefined,
        ),
      ),
    buildFileName: () =>
      `Procedure_Referral_${patientDefaults.lastName}_${patientDefaults.firstName}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`,
    getFormData: () =>
      ({
        ...form.values,
        recipient: form.values.recipient
          ? recipientFormData(form.values.recipient, {
              code: form.values.specialtyCode,
              label: form.values.specialtyLabel,
            })
          : undefined,
        selectedProviderNpi: form.values.recipient?.npi,
        selectedCptCodes: form.values.selectedCptCodes.map((c) => c.code),
        specialtyCode: form.values.specialtyCode,
        specialtyLabel: form.values.specialtyLabel,
        isEvaluationOnly: form.values.isEvaluationOnly,
        icd10Codes: form.values.diagnosisText
          .split(/[;\n]/)
          .map((s) => s.trim().split(/\s/)[0]?.toUpperCase())
          .filter(Boolean),
      }) as unknown as Record<string, unknown>,
    vendorName: 'Procedure Referral',
    laboratoryOrganizationName: 'Procedure Referral',
    facilityName: form.values.recipient ? recipientName(form.values.recipient) : undefined,
    diagnosisDefaults,
    getDiagnosisText: () => form.values.diagnosisText,
    orgError,
    overrideFaxNumber: form.values.recipient ? form.values.recipient.fax : null,
  })

  useEffect(() => {
    if (signature.savedSignature && !form.values.providerSignatureDataUrl) {
      form.setFieldValue('providerSignatureDataUrl', signature.savedSignature)
      form.setFieldValue(
        'providerSignatureDate',
        new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }),
      )
    }
  }, [signature.savedSignature]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form.values.indication || !indicationSuggestion) return
    form.setFieldValue('indication', indicationSuggestion)
  }, [indicationSuggestion, form.values.indication]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form.values.summary) return
    if (!patientDefaults.firstName || diagnosisDefaults.length === 0) return

    const dxList = diagnosisDefaults
      .slice(0, 10)
      .map((d) => `${d.code} ${d.display}`)
      .join(', ')

    const summaryText = `Patient ${patientDefaults.firstName} ${patientDefaults.lastName} (DOB: ${patientDefaults.dateOfBirth}, Sex: ${patientDefaults.sexCode || 'Unknown'}) presents with the following active conditions: ${dxList}. Please evaluate and advise on recommended procedure and treatment plan.`

    form.setFieldValue('summary', summaryText)
  }, [patientDefaults.firstName, diagnosisDefaults.length]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.values.isEvaluationOnly) return
    const already = form.values.selectedCptCodes.some((c) => c.code === GENERIC_REFERRAL_CPT.code)
    if (!already) {
      form.setFieldValue('selectedCptCodes', [GENERIC_REFERRAL_CPT])
    }
  }, [form.values.isEvaluationOnly]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignatureConfirm = (dataUrl: string, date: string) => {
    form.setFieldValue('providerSignatureDataUrl', dataUrl)
    form.setFieldValue('providerSignatureDate', date)
    signature.closeSignatureModal()
  }

  const handleSubmitOrder = async () => {
    if (!form.values.providerSignatureDataUrl) {
      notifications.show({
        title: 'Signature Required',
        message: 'Please sign the form before submitting.',
        color: 'red',
      })
      return
    }
    if (!reviewed) {
      notifications.show({
        title: 'Review required',
        message: 'Review the referral letter and clinical summary, then tick the confirmation box.',
        color: 'red',
      })
      return
    }
    await submission.handleSubmitOrder()
  }

  const handleSelectSpecialty = (code: string | null) => {
    const picked = NUCC_CLASSIFICATIONS.find((c) => c.code === code)
    form.setFieldValue('specialtyCode', picked?.code ?? '')
    form.setFieldValue('specialtyLabel', picked?.label ?? '')
    // Clear recipient + procedures when specialty changes
    form.setFieldValue('recipient', null)
    form.setFieldValue(
      'selectedCptCodes',
      form.values.isEvaluationOnly ? [GENERIC_REFERRAL_CPT] : [],
    )
  }

  const isCptSelected = (code: string) => form.values.selectedCptCodes.some((c) => c.code === code)

  const toggleCptCode = (cpt: CptCode) => {
    if (form.values.isEvaluationOnly) return
    if (isCptSelected(cpt.code)) {
      form.setFieldValue(
        'selectedCptCodes',
        form.values.selectedCptCodes.filter((c) => c.code !== cpt.code),
      )
    } else {
      form.setFieldValue('selectedCptCodes', [...form.values.selectedCptCodes, cpt])
    }
  }

  const filteredCptCodes = useMemo(() => {
    if (!cptSearch.trim()) return cptCodesForSpecialty
    const q = cptSearch.toLowerCase()
    return cptCodesForSpecialty.filter(
      (c) => c.code.toLowerCase().includes(q) || c.display.toLowerCase().includes(q),
    )
  }, [cptCodesForSpecialty, cptSearch])

  const stepIndex = STEPS.indexOf(step)
  const canAdvanceFromSpecialist = !!form.values.recipient && isValidFax(form.values.recipient.fax)
  const canAdvanceFromCpt = form.values.isEvaluationOnly || form.values.selectedCptCodes.length > 0

  // Seeded here, once: the Details step unmounts its section, which would re-seed on return.
  const diagnosisAutoFilled = useSeedDiagnosisCodes({
    currentValue: form.values.diagnosisText,
    defaults: diagnosisDefaults,
    onSeed: (v) => form.setFieldValue('diagnosisText', v),
  })
  const draftKey =
    form.values.specialtyCode && form.values.recipient
      ? `${form.values.specialtyCode}|${form.values.recipient.npi ?? form.values.recipient.placeId ?? recipientName(form.values.recipient)}`
      : null
  // Which specialty + recipient the current letter was drafted for, whether the clinician has
  // edited it since, and the review attestation (reset when the recipient/specialty changes).
  const recipient = form.values.recipient
  // Where the fax goes — another office of the same doctor, or an edited number — and what
  // it asks for: a change to either needs a new review even though the letter is unchanged.
  const procedures = form.values.selectedCptCodes.map((c) => c.code).join(',')
  const destination = recipient
    ? [
        recipient.npi,
        recipient.placeId,
        recipient.street,
        recipient.zip,
        recipient.fax,
        procedures,
      ].join('|')
    : ''
  const { reviewed, setReviewed, drafting, letterProps } = useReferralLetterState(
    draftKey,
    form.values.visitFindings,
    destination,
  )

  // Anything chosen is lost on a reload; the auto-filled signature and diagnoses are not input.
  useWarnBeforeUnload(
    !submission.orderSubmitted && Boolean(form.values.specialtyCode || form.values.recipient),
  )

  const handleBack = () => {
    const idx = STEPS.indexOf(step)
    if (idx === 0) onBack()
    else setStep(STEPS[idx - 1])
  }

  const cartItems: CartItem[] = useMemo(
    () =>
      form.values.selectedCptCodes.map((c) => ({
        id: c.code,
        code: c.code,
        name: c.display,
        category: form.values.isEvaluationOnly ? 'Consultation' : form.values.specialtyLabel,
      })),
    [form.values.selectedCptCodes, form.values.isEvaluationOnly, form.values.specialtyLabel],
  )

  const handleCartRemove = (id: string) => {
    // The evaluation-only 99242 is there because the option is ticked: its × unticks it,
    // so it leaves the order like any other code (PD-1867).
    if (form.values.isEvaluationOnly) form.setFieldValue('isEvaluationOnly', false)
    form.setFieldValue(
      'selectedCptCodes',
      form.values.selectedCptCodes.filter((c) => c.code !== id),
    )
  }

  return (
    <OrderFormLayout
      cart={
        <OrderCart items={cartItems} onRemove={handleCartRemove} vendorLabel="Procedure Referral" />
      }
      header={
        <>
          <FormHeader
            title="Procedure / Referral Order"
            subtitle="Find a specialist, select procedures, and send a referral"
            onBack={handleBack}
            onPreview={submission.handlePreviewPdf}
            onSubmit={handleSubmitOrder}
            exporting={submission.exporting}
            submitting={submission.submitting}
            orderSubmitted={submission.orderSubmitted}
            canSubmit={isPractitioner && step === 'review'}
            canOrderLoading={canOrderLoading}
            hideActions
            submitTooltip={
              !isPractitioner
                ? 'Practitioner permissions required to submit orders'
                : step !== 'review'
                  ? 'Complete all steps to submit'
                  : undefined
            }
            orgError={orgError}
            warnings={insuranceWarning ? [insuranceWarning] : []}
          />

          <Stepper active={stepIndex} mb="lg" size="sm">
            <Stepper.Step label="Specialist" description="Find a provider" />
            <Stepper.Step label="Procedures" description="Select CPT codes" />
            <Stepper.Step label="Details" description="Clinical info & signature" />
            <Stepper.Step label="Review" description="Review & submit" />
          </Stepper>
        </>
      }
    >
      {step === 'specialist' && (
        <Stack gap="lg">
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
              FIND A SPECIALIST
            </Text>
            <Select
              label="Specialty"
              placeholder="Select a specialty..."
              data={specialtyOptions}
              value={form.values.specialtyCode || null}
              onChange={handleSelectSpecialty}
              searchable
              clearable
              // Fits the longest name, Physical Medicine & Rehabilitation, beside the × (PD-1808).
              w={340}
              mb="md"
            />
            <SpecialistPicker
              patient={patient}
              specialtyCode={(form.values.specialtyCode || null) as ReferralSpecialtyCode | null}
              specialtyLabel={form.values.specialtyLabel}
              selected={form.values.recipient}
              onChange={(r) => form.setFieldValue('recipient', r)}
            />
          </Paper>

          <Group justify="flex-end">
            <Button variant="default" onClick={onBack}>
              Cancel
            </Button>
            <Button
              color="teal"
              onClick={() => setStep('cptCodes')}
              disabled={!canAdvanceFromSpecialist}
            >
              Next: Select Procedures
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'cptCodes' && (
        <Stack gap="lg">
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="sm" className="text-[#6B21A8]">
              SELECT PROCEDURE CODES
            </Text>

            <Checkbox
              label="No specific procedure — evaluation / transfer of care only"
              description={`Sends the referral with CPT ${GENERIC_REFERRAL_CPT.code} (${GENERIC_REFERRAL_CPT.display}) as the anticipated service.`}
              checked={form.values.isEvaluationOnly}
              onChange={(e) => form.setFieldValue('isEvaluationOnly', e.currentTarget.checked)}
              mb="md"
            />

            {!form.values.isEvaluationOnly && (
              <>
                <Text size="xs" c="dimmed" mb="md">
                  Select the CPT procedure codes for this referral. Search by code or description.
                </Text>

                <TextInput
                  placeholder="Search CPT codes..."
                  leftSection={<IconSearch size={16} />}
                  value={cptSearch}
                  onChange={(e) => setCptSearch(e.currentTarget.value)}
                  mb="md"
                  rightSection={
                    cptSearch ? (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label="Clear search"
                        onClick={() => setCptSearch('')}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    ) : null
                  }
                  rightSectionPointerEvents={cptSearch ? 'all' : 'none'}
                />

                {cptLoading && (
                  <Group justify="center" py="md">
                    <Loader size="sm" />
                  </Group>
                )}
                {!cptLoading && cptCodesForSpecialty.length === 0 && (
                  <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                    No CPT codes seeded for {form.values.specialtyLabel}. Ask a super-admin to run
                    seed-specialty-cpt-valuesets, or check the "evaluation only" box above.
                  </Alert>
                )}
                <Stack gap={4}>
                  {filteredCptCodes.map((cpt) => (
                    <Checkbox
                      key={cpt.code}
                      label={
                        <Text size="sm">
                          <Text component="span" fw={600}>
                            {cpt.code}
                          </Text>{' '}
                          — {cpt.display}
                        </Text>
                      }
                      checked={isCptSelected(cpt.code)}
                      onChange={() => toggleCptCode(cpt)}
                    />
                  ))}
                </Stack>
              </>
            )}
          </Paper>

          <Group justify="space-between">
            <Button variant="default" onClick={() => setStep('specialist')}>
              Back
            </Button>
            <Button color="teal" onClick={() => setStep('details')} disabled={!canAdvanceFromCpt}>
              Next: Referral Details
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'details' && (
        <Stack gap="lg">
          <DiagnosisCodesSection
            value={form.values.diagnosisText}
            autoFilled={diagnosisAutoFilled}
            onChange={(v) => form.setFieldValue('diagnosisText', v)}
          />

          <ReferralLetterFields
            draftInput={
              patient.id && form.values.specialtyCode
                ? {
                    patientId: patient.id,
                    kind: 'specialist',
                    specialtyCode: form.values.specialtyCode as ReferralSpecialtyCode,
                    ...(form.values.recipient?.lastName
                      ? {
                          recipient: {
                            firstName: form.values.recipient.firstName,
                            lastName: form.values.recipient.lastName,
                            credential: form.values.recipient.credential,
                          },
                        }
                      : {}),
                    visitFindings: form.values.visitFindings.trim() || undefined,
                  }
                : null
            }
            {...letterProps}
            autoDraft
            findings={form.values.visitFindings}
            onFindingsChange={(v) => form.setFieldValue('visitFindings', v)}
            letter={form.values.indication}
            onLetterChange={(v) => form.setFieldValue('indication', v)}
            summary={form.values.summary}
            onSummaryChange={(v) => form.setFieldValue('summary', v)}
          />

          <SignatureSection
            signatureDataUrl={form.values.providerSignatureDataUrl}
            signatureDate={form.values.providerSignatureDate}
            savedSignature={signature.savedSignature}
            signatureModalOpen={signature.signatureModalOpen}
            onOpenModal={signature.openSignatureModal}
            onCloseModal={signature.closeSignatureModal}
            onConfirm={handleSignatureConfirm}
            onSaveSignature={signature.handleSaveSignature}
            certificationText="I am a licensed healthcare provider and certify that this referral is medically necessary."
          />

          <Group justify="space-between">
            <Button variant="default" onClick={() => setStep('cptCodes')}>
              Back
            </Button>
            <TooltipButton
              color="teal"
              // The order panel can remove the last procedure after the Procedures step (PD-1824).
              disabled={
                submission.orderSubmitted ||
                !form.values.providerSignatureDataUrl ||
                !canAdvanceFromCpt ||
                drafting
              }
              disabledReason={
                !canAdvanceFromCpt
                  ? PROCEDURE_REQUIRED_TOOLTIP
                  : !form.values.providerSignatureDataUrl
                    ? SIGNATURE_REQUIRED_TOOLTIP
                    : drafting
                      ? DRAFTING_TOOLTIP
                      : null
              }
              onClick={() => setStep('review')}
            >
              Review Order
            </TooltipButton>
          </Group>
        </Stack>
      )}

      {step === 'review' && (
        <Stack gap="lg">
          <Paper p="md" withBorder>
            <Text fw={600} size="lg" mb="md">
              ORDER SUMMARY
            </Text>

            {form.values.recipient && (
              <>
                <Text fw={500} size="sm" mb="xs">
                  Referring To
                </Text>
                <div className="mb-4 rounded-md border border-gray-200 p-3">
                  <Group gap="xs" mb={2}>
                    <Text fw={600} size="sm">
                      {recipientName(form.values.recipient)}
                    </Text>
                    <Badge size="xs" variant="light" color="indigo">
                      {form.values.specialtyLabel}
                    </Badge>
                    {form.values.isEvaluationOnly && (
                      <Badge size="xs" variant="filled" color="grape">
                        Consultation only
                      </Badge>
                    )}
                  </Group>
                  {distinctPracticeName(form.values.recipient) && (
                    <Text size="xs" c="dimmed">
                      {distinctPracticeName(form.values.recipient)}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed">
                    {form.values.recipient.street}, {form.values.recipient.city},{' '}
                    {form.values.recipient.state} {form.values.recipient.zip}
                  </Text>
                  <Group gap="md" mt={4}>
                    {form.values.recipient.phone && (
                      <Text size="xs" c="dimmed">
                        Phone: {form.values.recipient.phone}
                      </Text>
                    )}
                    <Text size="xs" fw={600}>
                      Fax: {formatPhone(form.values.recipient.fax)}
                    </Text>
                    {form.values.recipient.npi && (
                      <Text size="xs" c="dimmed">
                        NPI: {form.values.recipient.npi}
                      </Text>
                    )}
                  </Group>
                </div>
              </>
            )}

            <Text fw={500} size="sm" mb="xs">
              Procedures ({form.values.selectedCptCodes.length})
            </Text>
            <Group gap={4} mb="md">
              {form.values.selectedCptCodes.map((cpt) => (
                <Badge key={cpt.code} size="sm" variant="light" color="indigo">
                  {cpt.code} — {cpt.display}
                </Badge>
              ))}
            </Group>

            {form.values.diagnosisText && (
              <>
                <Text fw={500} size="sm" mt="md" mb="xs">
                  Diagnosis Codes
                </Text>
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
                  {form.values.diagnosisText}
                </Text>
              </>
            )}

            {form.values.indication && (
              <>
                <Text fw={500} size="sm" mt="md" mb="xs">
                  Referral letter
                </Text>
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
                  {form.values.indication}
                </Text>
              </>
            )}

            {form.values.summary && (
              <>
                <Text fw={500} size="sm" mt="md" mb="xs">
                  Clinical Summary
                </Text>
                <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-line' }}>
                  {form.values.summary}
                </Text>
              </>
            )}

            {form.values.providerSignatureDataUrl && (
              <>
                <Text fw={500} size="sm" mt="md" mb="xs">
                  Provider Signature
                </Text>
                <img
                  src={form.values.providerSignatureDataUrl}
                  alt="Provider signature"
                  className="h-12 rounded border border-gray-200 p-1"
                />
                <Text size="xs" c="dimmed">
                  Signed: {form.values.providerSignatureDate}
                </Text>
              </>
            )}
          </Paper>

          <Checkbox
            label="I have reviewed the referral letter and clinical summary."
            checked={reviewed}
            onChange={(e) => setReviewed(e.currentTarget.checked)}
          />

          <Group justify="space-between" pb="xl">
            <Button variant="default" onClick={() => setStep('details')}>
              Back
            </Button>
            <Group gap="sm">
              <Button
                variant="outline"
                onClick={submission.handlePreviewPdf}
                loading={submission.exporting}
              >
                Download PDF
              </Button>
              <TooltipButton
                color="teal"
                loading={submission.submitting}
                disabled={
                  submission.orderSubmitted ||
                  !isPractitioner ||
                  !canAdvanceFromCpt ||
                  !form.values.providerSignatureDataUrl ||
                  !reviewed
                }
                disabledReason={
                  !isPractitioner
                    ? SUBMIT_PERMISSION_TOOLTIP
                    : !canAdvanceFromCpt
                      ? PROCEDURE_REQUIRED_TOOLTIP
                      : !form.values.providerSignatureDataUrl
                        ? SIGNATURE_REQUIRED_TOOLTIP
                        : !reviewed
                          ? 'Review the letter and clinical summary, then tick the box above.'
                          : null
                }
                onClick={handleSubmitOrder}
              >
                {submission.orderSubmitted ? 'Order Submitted' : 'Submit Order'}
              </TooltipButton>
            </Group>
          </Group>
        </Stack>
      )}
    </OrderFormLayout>
  )
}
